import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clientService from '../services/ClientService';

const ClientImportPage = () => {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const formatRowMessage = (message) => {
        const msg = String(message || '');

        // MySQL / SQLAlchemy típico cuando un valor excede el VARCHAR.
        const m = msg.match(/Data too long for column '([^']+)'/i);
        if (m) {
            return `El valor es demasiado largo para el campo '${m[1]}'. (Revisa el CSV: valores múltiples en una celda, textos largos, etc.)`;
        }

        // Reduce ruido si viene el SQL completo en el texto.
        const idx = msg.indexOf('[SQL:');
        if (idx !== -1) {
            return msg.slice(0, idx).trim();
        }

        return msg;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setResult(null);

        if (!file) {
            setError('Selecciona un archivo CSV.');
            return;
        }

        setLoading(true);
        try {
            const response = await clientService.importClientsCsv(file);
            setResult(response.data);
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail && typeof detail === 'object' && detail.missing_columns) {
                setError(`Faltan columnas: ${detail.missing_columns.join(', ')}`);
            } else {
                setError(detail || 'No se pudo importar el archivo.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mx-auto p-4">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Importación Masiva</h1>
                <button
                    onClick={() => navigate('/clients')}
                    className="border px-4 py-2 rounded hover:bg-gray-50"
                >
                    Volver
                </button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Archivo CSV</label>
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="block w-full"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                            Se detecta delimitador automáticamente (coma, punto y coma o tab).
                        </p>
                    </div>

                    {error && <p className="text-red-600">{error}</p>}

                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                    >
                        {loading ? 'Importando...' : 'Importar'}
                    </button>
                </form>

                {result && (
                    <div className="mt-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-2">Resultado</h2>
                        {Array.isArray(result.missing_columns) && result.missing_columns.length > 0 && (
                            <div className="mb-3 text-sm text-amber-700">
                                Columnas ausentes (opcionales): {result.missing_columns.join(', ')}
                            </div>
                        )}

                        {Array.isArray(result.notes) && result.notes.length > 0 && (
                            <div className="mb-3 text-sm text-gray-700">
                                {result.notes.map((n, i) => (
                                    <div key={i}>{n}</div>
                                ))}
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                            <div>Total filas: {result.total_rows}</div>
                            <div>Clientes creados: {result.clients_created}</div>
                            <div>Clientes actualizados: {result.clients_updated}</div>
                            <div>Servicios creados: {result.services_created}</div>
                            <div>Servicios actualizados: {result.services_updated}</div>
                            <div>Errores: {result.errors?.length || 0}</div>
                        </div>

                        {Array.isArray(result.errors) && result.errors.length > 0 && (
                            <div className="mt-4">
                                <h3 className="text-sm font-semibold text-gray-800 mb-2">Errores por fila</h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full bg-white">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="py-2 px-4 text-left">Fila</th>
                                                <th className="py-2 px-4 text-left">Cliente</th>
                                                <th className="py-2 px-4 text-left">Mensaje</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.errors.map((e, idx) => (
                                                <tr key={idx} className="border-b">
                                                    <td className="py-2 px-4">{e.row}</td>
                                                    <td className="py-2 px-4">{e.client_id || ''}</td>
                                                    <td className="py-2 px-4" title={e.message || ''}>{formatRowMessage(e.message)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientImportPage;
